// "Site scans" on the field app's map (2026-10-15,
// reports/ar_scan_mode_spec_2026-10-14.md section 19): the organisation's
// scanned sites as markers (maplibre clustering at low zoom; orange where a
// site has an as-built visit), capture tracks as dotted paths from zoom 17
// with heading arrows from zoom 18. Tap a site or a track dot to open the
// viewer (scanViewer.ts). Org-shared; the API is 404 for a GIS-only
// organisation and then nothing is drawn. Re-added on every style load.
import type { GeoJSONSource, Map as MlMap, MapMouseEvent } from "maplibre-gl";

import type { SiteSummary } from "../scanSitesCore";
import { headingArrows } from "../scanSitesCore";
import { openSiteViewer, siteFromUrl } from "../scanViewer";

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
const TRACK_ZOOM = 17;

type TrackPoint = [number, number, number | null, number];

let sitesFc: GeoJSON.FeatureCollection = EMPTY;
let tracksFc: GeoJSON.FeatureCollection = EMPTY;
let arrowsFc: GeoJSON.FeatureCollection = EMPTY;
let available = true;
let loadedAt = 0;
let tracksFor: [number, number, number, number] | null = null;

function src(map: MlMap, id: string): GeoJSONSource | null {
  return (map.getSource(id) as GeoJSONSource | undefined) ?? null;
}

/** Add the sources + layers (call from the map's style.load). */
export function addScanSiteLayers(map: MlMap): void {
  if (map.getSource("scan-sites")) return;
  map.addSource("scan-sites", { type: "geojson", data: sitesFc, cluster: true, clusterMaxZoom: 14, clusterRadius: 44 });
  map.addSource("scan-track", { type: "geojson", data: tracksFc });
  map.addSource("scan-arrows", { type: "geojson", data: arrowsFc });
  map.addLayer({ id: "scan-track-l", type: "circle", source: "scan-track", minzoom: TRACK_ZOOM,
    paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 17, 2, 20, 4], "circle-color": "#3182ce",
      "circle-stroke-color": "#fff", "circle-stroke-width": 1, "circle-opacity": 0.85 } });
  map.addLayer({ id: "scan-arrows-l", type: "line", source: "scan-arrows", minzoom: 18,
    paint: { "line-color": "#1a365d", "line-width": 1.6 } });
  map.addLayer({ id: "scan-clusters-l", type: "circle", source: "scan-sites", filter: ["has", "point_count"],
    paint: { "circle-color": "#2b6cb0", "circle-radius": ["step", ["get", "point_count"], 12, 5, 16, 20, 20],
      "circle-stroke-color": "#fff", "circle-stroke-width": 2, "circle-opacity": 0.9 } });
  if (map.getStyle().glyphs) {
    map.addLayer({ id: "scan-clusters-n", type: "symbol", source: "scan-sites", filter: ["has", "point_count"],
      layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 12, "text-font": ["Noto Sans Regular"] },
      paint: { "text-color": "#fff" } });
  }
  map.addLayer({ id: "scan-sites-l", type: "circle", source: "scan-sites", filter: ["!", ["has", "point_count"]],
    paint: { "circle-color": ["case", ["get", "as_built"], "#dd6b20", "#2b6cb0"], "circle-radius": 9,
      "circle-stroke-color": "#fff", "circle-stroke-width": 3 } });
  if (map.getStyle().glyphs) {
    map.addLayer({ id: "scan-sites-name", type: "symbol", source: "scan-sites", filter: ["!", ["has", "point_count"]], minzoom: 16,
      layout: { "text-field": ["get", "label"], "text-size": 12, "text-offset": [0, -1.6], "text-font": ["Noto Sans Regular"] },
      paint: { "text-color": "#1a202c", "text-halo-color": "#fff", "text-halo-width": 1.6 } });
  }
  void refreshScanSites(map);
}

/** (Re)load the organisation's sites; at most once a minute unless forced. */
export async function refreshScanSites(map: MlMap, force = false): Promise<void> {
  if (!available || (!force && Date.now() - loadedAt < 60_000 && sitesFc.features.length)) {
    src(map, "scan-sites")?.setData(sitesFc);
    return;
  }
  try {
    const res = await fetch("/api/field/scans/sites", { credentials: "same-origin" });
    if (res.status === 404) {
      available = false;
      return;
    }
    if (!res.ok) return;
    const sites = ((await res.json()) as { sites: SiteSummary[] }).sites;
    loadedAt = Date.now();
    sitesFc = {
      type: "FeatureCollection",
      features: sites.map((s) => ({ type: "Feature", geometry: { type: "Point", coordinates: [s.lon, s.lat] },
        properties: { site_id: s.id, label: s.has_as_built ? `${s.name} (as-built)` : s.name, as_built: !!s.has_as_built } })),
    };
    src(map, "scan-sites")?.setData(sitesFc);
    const link = siteFromUrl();
    if (link?.view && sites.some((s) => s.id === link.id)) void openSiteViewer(link.id, { session: link.session });
  } catch {
    /* offline: keep what we had */
  }
}

/** On moveend: capture tracks for the view from zoom 17. */
export async function updateScanTracks(map: MlMap): Promise<void> {
  if (!available || map.getZoom() < TRACK_ZOOM || !sitesFc.features.length) return;
  const b = map.getBounds();
  const view: [number, number, number, number] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  if (tracksFor && view[0] >= tracksFor[0] && view[1] >= tracksFor[1] && view[2] <= tracksFor[2] && view[3] <= tracksFor[3]) return;
  const px = (view[2] - view[0]) / 2, py = (view[3] - view[1]) / 2;
  const want: [number, number, number, number] = [view[0] - px, view[1] - py, view[2] + px, view[3] + py];
  try {
    const res = await fetch(`/api/field/scans/tracks?bbox=${want.map((v) => v.toFixed(6)).join(",")}`, { credentials: "same-origin" });
    if (!res.ok) return;
    const tracks = ((await res.json()) as { tracks: { site_id: number; session: string; points: TrackPoint[] }[] }).tracks;
    tracksFor = want;
    tracksFc = {
      type: "FeatureCollection",
      features: tracks.flatMap((t) => t.points.map((p) => ({ type: "Feature" as const, geometry: { type: "Point" as const, coordinates: [p[0], p[1]] },
        properties: { site_id: t.site_id, session: t.session, idx: p[3] } }))),
    };
    arrowsFc = {
      type: "FeatureCollection",
      features: tracks.flatMap((t) => headingArrows(t.points, 2, 1.6).map((a) => ({ type: "Feature" as const,
        geometry: { type: "LineString" as const, coordinates: a }, properties: {} }))),
    };
    src(map, "scan-track")?.setData(tracksFc);
    src(map, "scan-arrows")?.setData(arrowsFc);
  } catch {
    /* offline */
  }
}

/** A tap on a site / cluster / track dot. True when handled. */
export function scanSiteClick(map: MlMap, e: MapMouseEvent): boolean {
  const layers = ["scan-sites-l", "scan-clusters-l", "scan-track-l"].filter((l) => map.getLayer(l));
  if (!layers.length) return false;
  const box: [[number, number], [number, number]] = [[e.point.x - 10, e.point.y - 10], [e.point.x + 10, e.point.y + 10]];
  const f = map.queryRenderedFeatures(box, { layers })[0];
  if (!f) return false;
  const p = f.properties ?? {};
  if (f.layer.id === "scan-clusters-l") {
    map.easeTo({ center: (f.geometry as GeoJSON.Point).coordinates as [number, number], zoom: map.getZoom() + 2 });
    return true;
  }
  if (f.layer.id === "scan-track-l") {
    void openSiteViewer(Number(p.site_id), { session: String(p.session), idx: Number(p.idx) });
    return true;
  }
  void openSiteViewer(Number(p.site_id));
  return true;
}
